import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";
import type { ProcessedWidgetData } from "../../Processing/types";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { GroupByBar } from "../shared/GroupByBar";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { usePlotContext } from "./PlotContext";

const { Text, Title } = Typography;

interface PlotPreviewPanelProps {
  previewData: ProcessedWidgetData | null;
}

export function PlotPreviewPanel({ previewData }: PlotPreviewPanelProps) {
  const { state, dispatch, metrics } = usePlotContext();
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const [chartHeight, setChartHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = Math.floor(entry.contentRect.height);
      if (h > 0) setChartHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hasMetric = state.graphs.some((g) => g.selectedMetricId != null);
  const firstMetricName = metrics.find((m) => m.id === state.graphs[0]?.selectedMetricId)?.name;
  const displayTitle = state.title || firstMetricName || "Plot Preview";

  // Read the summary off the PROCESSED spec, not the raw form state: a plot that follows a
  // Slicer has already had the controller's dates/subjects substituted there,
  // so this line agrees with the chart beside it instead of quoting dormant values.
  const summaryParts = useMemo(() => {
    const miscs =
      previewData?.type === "chart"
        ? previewData.spec.series.map((s) => s.series_builder_misc)
        : state.graphs;
    const ranges = [...new Set(miscs.map((m) => resolveDateRange(m)))];
    const totalSelected = miscs.reduce(
      (sum, m) => sum + (m.selectedSubjects?.length ?? 0) + (m.selectedDrills?.length ?? 0),
      0,
    );
    const subjectSummary = totalSelected > 0 ? `${totalSelected} selected` : "Full Team";
    return [
      `${state.graphs.length} plot${state.graphs.length > 1 ? "s" : ""}`,
      ranges.join(", "),
      subjectSummary,
    ];
  }, [state.graphs, previewData]);

  const MOCK_GENERATORS: Record<string, (i: number) => string> = {
    player: (i) => `Player ${i + 1}`,
    position: (i) => ["QB", "RB", "WR", "LB", "DB", "TE", "OL", "DL", "S", "K"][i % 10],
    drill: (i) => `Drill ${i + 1}`,
    day: (i) => { const d = new Date(2026, 2, 13 + i); return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; },
    week: (i) => `2026-W${String(10 + i).padStart(2, "0")}`,
    month: (i) => { const m = (11 + i) % 12 + 1; const y = 2025 + Math.floor((11 + i) / 12); return `${y}-${String(m).padStart(2, "0")}`; },
    year: (i) => String(2022 + i),
  };

  const chartOptions = useMemo(() => {
    if (!previewData || previewData.type !== "chart") return null;
    const base = previewData.chart.chartOptions;

    const realCats = (base.xAxis as { categories?: string[] })?.categories;
    const gen = MOCK_GENERATORS[state.groupBy] ?? MOCK_GENERATORS.day;
    // When a custom sort is active, keep the real (sorted) labels so the reordering
    // is visible. Otherwise prettify with mock-gen labels for visual polish.
    const categories = realCats
      ? state.sortBy !== null
        ? realCats
        : realCats.map((_, i) => gen(i))
      : undefined;

    return {
      ...base,
      chart: {
        ...((base.chart as Record<string, unknown>) ?? {}),
        height: chartHeight ?? null,
        reflow: true,
        animation: false,
      },
      title: { text: "" },
      xAxis: {
        ...((base.xAxis as Record<string, unknown>) ?? {}),
        ...(categories && { categories }),
      },
      legend: {
        ...((base.legend as Record<string, unknown>) ?? {}),
        enabled: state.showLegend,
        align: "center" as const,
        verticalAlign: "top" as const,
        layout: "horizontal" as const,
      },
      exporting: { enabled: false },
    };
  }, [previewData, state.showLegend, state.groupBy, state.sortBy, chartHeight]);

  return (
    <>
      <div className="awm-preview-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <Title level={5} style={{ margin: 0, flexShrink: 0 }}>
            {displayTitle}
          </Title>
          {hasMetric && <PreviewDisclaimer />}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summaryParts.join(" · ")}
        </Text>

        <div className="awm-preview-chart-area" ref={chartAreaRef}>
          {hasMetric && chartOptions ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartRef}
              immutable
              containerProps={{
                style: { width: "100%", height: "100%", minHeight: 0 },
              }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Pick a metric to see a preview"
            />
          )}
        </div>
      </div>

      <GroupByBar
        value={state.groupBy}
        disabledGroupBys={
          state.graphs.some((g) => g.countSpec)
            ? { drill: "A count cannot be grouped by drill: none of the counted tables carries a drill relation." }
            : undefined
        }
        onChange={(groupBy) => dispatch({ type: "SET_GROUP_BY", groupBy })}
        sort={{
          value: state.sortBy,
          onValueChange: (sortBy) => dispatch({ type: "SET_SORT_BY", sortBy }),
          seriesOptions: (() => {
            const configured = state.graphs
              .map((g, i) => ({ g, plotNumber: i + 1 }))
              .filter(({ g }) => g.selectedMetricId != null);
            const plotOptions = configured.map(({ g, plotNumber }) => ({
              id: g.id,
              label: `(Plot ${plotNumber}) ${
                metrics.find((m) => m.id === g.selectedMetricId)?.name ?? "Plot"
              }`,
            }));
            let stackNum = 0;
            const stackOptions = configured
              .filter(({ g }) =>
                state.graphs.some((other) => other.stacked && other.stackOnGraphId === g.id),
              )
              .map(({ g }) => {
                stackNum += 1;
                return {
                  id: `stacked:${g.id}`,
                  label: `(Stack ${stackNum}) ${
                    metrics.find((m) => m.id === g.selectedMetricId)?.name ?? "Stack"
                  }`,
                };
              });
            return [...plotOptions, ...stackOptions];
          })(),
          seriesId:
            state.sortIncludeStacked && state.sortSeriesId
              ? `stacked:${state.sortSeriesId}`
              : state.sortSeriesId,
          onSeriesIdChange: (id) => {
            if (id.startsWith("stacked:")) {
              dispatch({ type: "SET_SORT_SERIES_ID", id: id.slice("stacked:".length) });
              dispatch({ type: "SET_SORT_INCLUDE_STACKED", value: true });
            } else {
              dispatch({ type: "SET_SORT_SERIES_ID", id });
              dispatch({ type: "SET_SORT_INCLUDE_STACKED", value: false });
            }
          },
        }}
      />

      <Text
        type="secondary"
        style={{ display: "block", textAlign: "center", marginTop: 6, fontSize: 12 }}
      >
        {!hasMetric
          ? "Pick a metric to see a preview..."
          : `${state.graphs.filter((g) => g.selectedMetricId != null).length} of ${state.graphs.length} plots configured`}
      </Text>
    </>
  );
}
